import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import posthog from "posthog-js";
import CrispChat from "./components/CrispChat";
import ProgressBar from "./components/ProgressBar";
import { useAuth } from "./context/AuthContext";
import { useLocation } from "~/lib/team-router";
import "./main.css";

declare global {
  interface Window {
    clarity?: (
      method: string,
      customId?: string,
      customSessionId?: string,
      customPageId?: string,
      friendlyName?: string
    ) => Promise<{
      id: string;
      session?: string;
      page?: string;
      userHint?: string;
    }> | void;
  }
}

interface ErrorBoundaryFallbackProps {
  resetError: () => void;
}

export function ErrorBoundaryFallback({
  resetError,
}: ErrorBoundaryFallbackProps) {
  const { isAuthenticated, logout } = useAuth();

  const signOutAndReload = () => {
    void (async () => {
      await logout(false);
      window.location.assign("/login");
    })();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="border-0 shadow-lg bg-white rounded-xl">
          <div className="py-10 px-8 text-center">
            <div className="text-8xl md:text-9xl font-bold text-gray-200 select-none">
              Oops!
            </div>
            <div className="-mt-10 md:-mt-20 flex items-center justify-center">
              <img
                src="/mascot.png"
                alt="Phoenix Mascot"
                className="w-60 h-48 md:w-80 md:h-60"
              />
            </div>

            <p className="my-10 text-gray-500 mb-8 max-w-sm mx-auto">
              {`Something unexpected happened. Don't worry, our team has been
              notified.`}
            </p>

            <div className="mt-20 flex flex-wrap gap-4 justify-center items-center">
              <button
                type="button"
                onClick={resetError}
                className="py-2 px-4 font-semibold text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 flex items-center gap-2 cursor-pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>Try Again</span>
              </button>
              {isAuthenticated ? (
                <button
                  type="button"
                  onClick={signOutAndReload}
                  className="py-2 px-4 font-semibold text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 flex items-center gap-2 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 shrink-0" />
                  <span>Logout</span>
                </button>
              ) : null}
              <a
                href="/"
                className="py-2 px-4 font-semibold text-sm rounded-lg bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white flex items-center gap-2 cursor-pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span>Go Home</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            If this keeps happening, please contact our support team.
          </p>
        </div>
      </div>
    </div>
  );
}

interface AppProps {
  children: ReactNode;
}

export default function App({ children }: AppProps) {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [clarityLoaded, setClarityLoaded] = useState(false);

  const isClarityEnabled =
    import.meta.env.MODE === "production" && import.meta.env.VITE_CLARITY_ID;
  const isPostHogEnabled =
    import.meta.env.MODE === "production" && import.meta.env.VITE_POSTHOG_KEY;

  useEffect(() => {
    if (isPostHogEnabled) {
      posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_POSTHOG_HOST,
        defaults: "2026-08-30",
      });
    }
  }, [isPostHogEnabled]);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      posthog.identify(String(user.id), {
        email: user.email,
        name: [user.first_name, user.last_name].filter(Boolean).join(" "),
      });
    }
  }, [
    isAuthenticated,
    user?.id,
    user?.email,
    user?.first_name,
    user?.last_name,
  ]);

  useEffect(() => {
    if (isClarityEnabled) {
      import("@microsoft/clarity")
        .then((Clarity) => {
          Clarity.default.init(import.meta.env.VITE_CLARITY_ID);
          setClarityLoaded(true);
        })
        .catch((error) => {
          console.warn("Failed to initialize Clarity:", error);
        });
    }
  }, [isClarityEnabled]);

  useEffect(() => {
    if (isClarityEnabled && clarityLoaded && isAuthenticated && user?.email) {
      try {
        if (typeof window.clarity !== "undefined") {
          window.clarity(
            "identify",
            user.email,
            undefined,
            undefined,
            user.first_name || user.email.split("@")[0]
          );
        }
      } catch (error) {
        console.warn("Failed to identify user in Clarity:", error);
      }
    }
  }, [isClarityEnabled, clarityLoaded, isAuthenticated, user]);

  useEffect(() => {
    if (
      isClarityEnabled &&
      clarityLoaded &&
      typeof window.clarity !== "undefined"
    ) {
      try {
        window.clarity("set", "page", location.pathname);
      } catch (error) {
        console.warn("Failed to track page change in Clarity:", error);
      }
    }
  }, [isClarityEnabled, clarityLoaded, location.pathname]);

  return (
    <div className="h-full">
      <ProgressBar />
      <CrispChat />
      {children}
    </div>
  );
}
